

































































import {
  defineComponent,
  ref,
  useFetch,
  useRoute,
  onMounted,
  computed,
  useContext,
  watch,
} from '@nuxtjs/composition-api';
import dayjs from 'dayjs';
import urlJoin from 'url-join';
import BlogSideMenu from '~/components/pages/Blog/BlogSideMenu.vue';
import AppIcon from '~/components/partials/App/AppIcon.vue';
import AppImage from '~/components/partials/App/AppImage.vue';
import AppLink from '~/components/partials/App/AppLink.vue';
import AppLoader from '~/components/partials/App/AppLoader.vue';
import ContentDynamicComponent from '~/components/partials/Content/ContentDynamicComponent.vue';
import ContentRelatedLinks from '~/components/partials/Content/ContentRelatedLinks.vue';
import ContentTagsAndShareIcons from '~/components/partials/Content/ContentTagsAndShareIcons.vue';
import ContentLayout from '~/components/partials/Layout/ContentLayout.vue';
import { useFilterBlogList } from '~/composables/blog/useFilterBlogList';
import { useLayoutProps } from '~/composables/useLayoutProps';
import { useRuntimeConfig } from '~/composables/useRuntimeConfig';
import { useStore } from '~/composables/useStore';
import { url, karteTrack, extractText } from '~/utils';
import { PageError } from '~/utils/error';
import { ECSchema, CMSSchema } from '~/types/api';
import { PastArticle } from '~/types/modules/blog';

export default defineComponent({
  components: {
    AppLoader,
    BlogSideMenu,
    ContentDynamicComponent,
    ContentLayout,
    ContentRelatedLinks,
    ContentTagsAndShareIcons,
    AppIcon,
    AppImage,
    AppLink,
  },
  setup() {
    const store = useStore();
    const route = useRoute();
    const { i18n } = useContext();
    const { ENABLED_BLOG, FRONTEND_ORIGIN } = useRuntimeConfig();
    const { fetchPastArticles, fetchStaffList, staffList, staffShopId } = useFilterBlogList();
    const fallbackImage = require('~/assets/images/content-ogp-fallback.png');
    const shopDetail = ref<ECSchema<'saleslocation.ShowResponse'>>();
    const isInvalidImagePath = ref(false);
    const pastArticles = ref<PastArticle[]>([]);
    const articleDetail = ref<CMSSchema<'swagger.BaseArticleInfo'>>();
    const shopPermalink = route.value.params.permalink;
    const articleId = route.value.params.articleId;
    let shopId = '';

    const { fetchState } = useFetch(async () => {
      try {
        if (ENABLED_BLOG === 'disabled') throw new Error('Blog disabled');
        // 記事取得（API側で公開開始と終了で絞り込んでいるので、公開でその周りのバリデーションは不要）
        articleDetail.value = await store.dispatch('modules/content/fetchArticle', {
          id: articleId,
        });
        // 記事がないと、404 画面に遷移する
        if (!articleDetail.value?.article_id) throw new Error('No article found');
        // 記事が公開されていないと、404 画面に遷移する
        // TODO: 型修正待ち
        // @ts-ignore
        if (!articleDetail.value.contents?.detail_display_flag) throw new Error('Article not available');
        await fetchShopDetail();
        // fetchShopDetail() 後に叩く
        await handleFetchPastArticles();
        await fetchStaffList(shopDetail.value);
      } catch {
        throw new PageError(404, {});
      }
    });

    onMounted(() => {
      // レコメの人気順のための集計
      window.TLAB.push(['Beacon::PutAccessLog', articleId]);
    });

    watch(
      () => articleDetail.value,
      () => {
        // KARTE
        karteTrack('_view_blog', {
          category_id: shopId,
          category_name: articleDetail.value?.title,
          category_url: urlJoin(FRONTEND_ORIGIN, url('BLOG_DETAIL', { permalink: shopPermalink, articleId })),
          page_type: shopDetail.value?.place_short_name,
          open_from_dtime: articleDetail.value?.publish_at,
          open_to_dtime: articleDetail.value?.expire_at,
        });
      }
    );

    // 店舗情報取得
    const fetchShopDetail = async () => {
      shopDetail.value = await store.dispatch('modules/salesLocation/fetchSalesLocationByID', {
        path: { id: 0 },
        query: { is_permalink: true, target_permalink: shopPermalink },
      });
      shopId = shopDetail.value.id.toString();
    };

    // 過去のブログ公開日データ取得
    const handleFetchPastArticles = async () => {
      pastArticles.value = await fetchPastArticles(shopId);
    };

    const dynamicParts = computed(() => articleDetail.value?.contents?.__dynamic_parts || []);
    const mainImage = computed(() => {
      // todo: API形修正待ち
      // @ts-ignore
      return articleDetail.value?.contents?.main_image || '';
    });
    const description = computed(() => {
      // todo: API形修正待ち
      // @ts-ignore
      return articleDetail.value?.contents?.description || '';
    });
    const shopName = computed(() => {
      // todo: API形修正待ち
      // @ts-ignore
      return articleDetail.value?.contents?.author_shop || '';
    });
    const authorName = computed(() => {
      // todo: API形修正待ち
      // @ts-ignore
      return articleDetail.value?.contents?.author_name || '';
    });

    // 見出しパーツ
    const headlines = computed(() => {
      const filteredHeadlines = dynamicParts.value.filter(
        (dynamicParts: any) => dynamicParts.parts === 'headline_without_option' && !!dynamicParts.items.text
      );

      return filteredHeadlines.reduce((acc: any[], cur: any) => {
        const { headline_style, text } = cur.items;

        if (headline_style === 'big') {
          acc.push({
            text,
            middles: [],
          });

          return acc;
        }

        if (!acc[acc.length - 1]) {
          acc.push({
            text: '',
            middles: [],
          });
        }

        acc[acc.length - 1].middles.push({
          text,
        });

        return acc;
      }, []);
    });

    // 一番目のテキストパーツを取得する
    const getLeadingText = () => {
      // 一番目のダイナミックパーツが 'body_text'の場合、それをSEOのdescriptionとして設定
      // それ以外の場合、設定しない
      if (!dynamicParts.value.length) return '';
      const firstDynamicPart = dynamicParts.value[0];
      if (firstDynamicPart.parts !== 'body_text') return '';
      // @ts-ignore
      return extractText(firstDynamicPart.items.text);
    };

    useLayoutProps(() => {
      const shopName = shopDetail.value?.place_short_name || shopDetail.value?.place_long_name || '';
      return {
        fetchState,
        layout: {
          headerMenu: 'never',
        },
        head: {
          title: articleDetail.value?.title,
          description: i18n.t('Other-33.seo.pageDescription', { shopName, description: getLeadingText() }).toString(),
        },
        breadcrumbs: [
          { title: i18n.t('Other-02.pageTitle').toString(), to: { path: url('SHOP_LIST') } },
          {
            title: i18n.t('Other-34.title').toString(),
            to: { path: url('BLOG_LIST') },
          },
          {
            title: i18n
              .t('Other-32.pageTitle', {
                shopName: shopDetail.value?.place_short_name || shopDetail.value?.place_long_name,
              })
              .toString(),
            to: { path: url('BLOG_TOP', { permalink: shopDetail.value?.permalink }) },
          },
          {
            title: articleDetail.value?.title || '',
            to: null,
          },
        ],
      };
    });

    return {
      dayjs,
      url,
      fallbackImage,
      isInvalidImagePath,
      fetchState,
      shopId,
      shopPermalink,
      articleId,
      articleDetail,
      dynamicParts,
      mainImage,
      description,
      shopName,
      authorName,
      shopDetail,
      staffList,
      staffShopId,
      pastArticles,
      headlines,
    };
  },
});
